import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Select,
  message,
  Space,
  InputNumber,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import FinancialYearService from "../../../services/FinancialYearService";
import IncomeExpenditureService from "../../../services/IncomeExpenditureService";

// Expenditure title options based on common expenditure categories
const EXPENDITURE_TITLE_OPTIONS = [
  {
    value: "Development Works & Infrastructure",
    label: "विकास कार्य व पायाभूत सुविधा (Development Works & Infrastructure)",
  },
  { value: "Education", label: "शिक्षण (Education)" },
  {
    value: "Salary and Allowances",
    label: "वेतन आणि भत्ते (Salary and Allowances)",
  },
  { value: "Health Services", label: "आरोग्य सेवा (Health Services)" },
  { value: "Sanitation", label: "स्वच्छता (Sanitation)" },
  { value: "Disabled Welfare (5%)", label: "दिव्यांग (5%) (Disabled Welfare)" },
  {
    value: "Backward Classes (15%)",
    label: "मागासवर्गीय (15%) (Backward Classes)",
  },
  {
    value: "Women & Child Welfare (10%)",
    label: "महिला बाल कल्याण (10%) (Women & Child Welfare)",
  },
  { value: "Other", label: "इतर (Other)" },
];

const Expenditure = () => {
  const [list, setList] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await IncomeExpenditureService.getExpenditureItems();
      const data = res.data;
      data.sort((a, b) => a.id - b.id);
      console.log(data);
      setList(data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load expenditure data");
    } finally {
      setLoading(false);
    }
  };

  const fetchYears = async () => {
    try {
      const res = await FinancialYearService.getFinancialYears();
      setYears(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load financial years");
    }
  };

  useEffect(() => {
    fetchData();
    fetchYears();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ language: "Marathi" });
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      title: record.title,
      percentage: record.percentage,
      financial_year: record.financial_year,
      amount: record.amount,
      received_amount: record.received_amount,
      spent_amount: record.spent_amount,
      language: record.language || "Marathi",
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete expenditure record",
      content: "Are you sure you want to delete this record?",
      async onOk() {
        try {
          await IncomeExpenditureService.deleteExpenditureItem(id);
          message.success("Deleted successfully");
          fetchData();
        } catch (err) {
          console.error(err);
          message.error("Delete failed");
        }
      },
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const onFinish = async (values) => {
    const payload = {
      title: values.title,
      percentage: values.percentage || null,
      financial_year: values.financial_year,
      amount: values.amount || null,
      received_amount: values.received_amount || null,
      spent_amount: values.spent_amount || null,
      language: values.language || "Marathi",
    };

    setLoading(true);
    try {
      if (editing) {
        await IncomeExpenditureService.updateExpenditureItem(
          editing.id,
          payload
        );
        message.success("Updated successfully");
      } else {
        await IncomeExpenditureService.createExpenditureItem(payload);
        message.success("Created successfully");
      }
      setIsModalOpen(false);
      form.resetFields();
      fetchData();
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const getYearName = useCallback(
    (id) => {
      const y = years.find((x) => x.id === id);
      const yearName = y ? y.name || y.year_name || id : id;
      return yearName ? `FY ${yearName}` : "-";
    },
    [years]
  );

  // Group data by financial year
  const groupedData = useMemo(() => {
    const groups = {};
    list.forEach((item) => {
      const fyId = item.financial_year;
      if (!groups[fyId]) {
        groups[fyId] = [];
      }
      groups[fyId].push(item);
    });
    return Object.entries(groups).map(([fyId, items]) => ({
      key: `fy-${fyId}`,
      fyId: Number(fyId),
      fyName: getYearName(Number(fyId)),
      items,
      totalAmount: items.reduce((sum, i) => sum + (Number(i.amount) || 0), 0),
      totalSpent: items.reduce(
        (sum, i) => sum + (Number(i.spent_amount) || 0),
        0
      ),
    }));
  }, [list, getYearName]);

  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v) => (v ? `₹${Number(v).toLocaleString("en-IN")}` : "-"),
    },
    {
      title: "Received Amount",
      dataIndex: "received_amount",
      key: "received_amount",
      render: (v) => (v ? `₹${Number(v).toLocaleString("en-IN")}` : "-"),
    },
    {
      title: "Spent Amount",
      dataIndex: "spent_amount",
      key: "spent_amount",
      render: (v) => (v ? `₹${Number(v).toLocaleString("en-IN")}` : "-"),
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
      render: (v) => (v ? `${v}%` : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, r) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(r)}
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(r)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(r.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>Expenditure</h3>
        <div style={{ flexShrink: 0 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Expenditure
          </Button>
        </div>
      </div>

      {groupedData.length === 0 ? (
        <Table columns={columns} dataSource={[]} loading={loading} />
      ) : (
        groupedData.map((group) => (
          <div key={group.key} style={{ marginBottom: 24 }}>
            <div
              style={{
                background: "linear-gradient(90deg, #001529 0%, #042035 100%)",
                padding: "12px 16px",
                borderRadius: "8px 8px 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ color: "white", fontWeight: 600, fontSize: 16 }}>
                {group.fyName}
              </span>
              <span style={{ color: "rgba(255,255,255,0.9)", fontSize: 14 }}>
                Total: ₹{group.totalAmount.toLocaleString("en-IN")} | Spent: ₹
                {group.totalSpent.toLocaleString("en-IN")}
              </span>
            </div>
            <Table
              columns={columns}
              dataSource={group.items}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
              style={{ borderRadius: "0 0 8px 8px", overflow: "hidden" }}
            />
          </div>
        ))
      )}

      <Modal
        title={editing ? "Edit Expenditure" : "Add Expenditure"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ language: "Marathi" }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please select title" }]}
          >
            <Select
              showSearch
              placeholder="Select expenditure type"
              options={EXPENDITURE_TITLE_OPTIONS}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="financial_year"
            label="Financial Year"
            rules={[{ required: true, message: "Select financial year" }]}
          >
            <Select
              showSearch
              placeholder="Select year"
              options={years.map((y) => ({
                label: `FY ${y.name || y.year_name}`,
                value: y.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="amount" label="Amount">
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Enter amount"
              min={0}
            />
          </Form.Item>
          <Form.Item name="received_amount" label="Received Amount">
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Enter received amount"
              min={0}
            />
          </Form.Item>
          <Form.Item name="spent_amount" label="Spent Amount">
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Enter spent amount"
              min={0}
            />
          </Form.Item>
          <Form.Item name="percentage" label="Percentage">
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Enter percentage"
              min={0}
              max={100}
              step={0.01}
            />
          </Form.Item>
          <Form.Item name="language" label="Language">
            <Select
              options={[
                { label: "English", value: "English" },
                { label: "Marathi", value: "Marathi" },
                { label: "Hindi", value: "Hindi" },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editing ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Expenditure Details"
        open={isViewOpen}
        onCancel={() => setIsViewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {viewing && (
          <div>
            <p>
              <strong>Title:</strong> {viewing.title}
            </p>
            <p>
              <strong>Financial Year:</strong>{" "}
              {getYearName(viewing.financial_year)}
            </p>
            <p>
              <strong>Amount:</strong>{" "}
              {viewing.amount
                ? `₹${Number(viewing.amount).toLocaleString("en-IN")}`
                : "-"}
            </p>
            <p>
              <strong>Received Amount:</strong>{" "}
              {viewing.received_amount
                ? `₹${Number(viewing.received_amount).toLocaleString("en-IN")}`
                : "-"}
            </p>
            <p>
              <strong>Spent Amount:</strong>{" "}
              {viewing.spent_amount
                ? `₹${Number(viewing.spent_amount).toLocaleString("en-IN")}`
                : "-"}
            </p>
            <p>
              <strong>Percentage:</strong>{" "}
              {viewing.percentage ? `${viewing.percentage}%` : "-"}
            </p>
            <p>
              <strong>Language:</strong> {viewing.language}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Expenditure;
